import { CostUnit, RehabAction, isCustomCostAction } from '@Types';

const FEET_PER_METER = 3.28084;

export interface FormatCostBreakdownArgs {
	rehabAction: RehabAction;
	costCalculation: string;
	totalCost: number;
	system: 'Metric' | 'Imperial';
	t: (key: string, options?: Record<string, unknown>) => string;
	formatCurrency: (value: number) => string;
}

export interface CostBreakdown {
	value: string;
	breakdown: string;
}

/** Extract the number inside the LAST [...] group of a backend cost string. */
const parseLastBracketNumber = (source: string): number | null => {
	const close = source.lastIndexOf(']');
	if (close === -1) return null;
	const open = source.lastIndexOf('[', close);
	if (open === -1) return null;
	const raw = source.slice(open + 1, close).replaceAll(/[$,\s]/g, '');
	const num = Number.parseFloat(raw);
	return Number.isNaN(num) ? null : num;
};

export const formatCostBreakdown = ({
	rehabAction,
	costCalculation,
	totalCost,
	system,
	t,
	formatCurrency,
}: FormatCostBreakdownArgs): CostBreakdown => {
	const value = formatCurrency(totalCost);

	// Cost-table actions carry no unit cost; show a simple label.
	if (!isCustomCostAction(rehabAction)) {
		return { value, breakdown: t('Cost table') };
	}

	const unitCost =
		system === 'Imperial'
			? rehabAction.unitCost / FEET_PER_METER
			: rehabAction.unitCost;
	const unitCostStr = formatCurrency(unitCost);

	const parsed = parseLastBracketNumber(costCalculation);
	// Fallback: cannot read the multiplier — never show worse than the backend string.
	if (parsed === null) {
		return { value, breakdown: costCalculation };
	}

	if (rehabAction.unit === CostUnit.length) {
		return {
			value,
			breakdown: t(
				'Unit Cost [{{value}}] × Length [{{length}} {{unit}}]',
				{
					value: unitCostStr,
					length: parsed.toFixed(2),
					// Unit symbols are locale-independent; keep them as plain literals.
					unit: system === 'Imperial' ? 'ft' : 'm',
				},
			),
		};
	}

	return {
		value,
		breakdown: t('Unit Cost [{{value}}] × Quantity [{{quantity}}]', {
			value: unitCostStr,
			quantity: parsed,
		}),
	};
};
